import { Controller } from '@hotwired/stimulus';

// Bootstrap 3 の CSS のまま、jQuery の Bootstrap プラグインと同じ要素を body に足して出す
// Bootstrap 3 の .tooltip.fade のトランジション
const TRANSITION_DURATION = 150;

export default class extends Controller {
  connect() {
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    // Bootstrap 3 の data-trigger と同じく、既定は hover と focus の両方で出す
    const triggers = (this.element.dataset.trigger || 'hover focus').split(' ');
    this.events = [
      ...(triggers.includes('hover')
        ? [
            ['mouseenter', this.show],
            ['mouseleave', this.hide],
          ]
        : []),
      ...(triggers.includes('focus')
        ? [
            ['focusin', this.show],
            ['focusout', this.hide],
          ]
        : []),
    ];
    for (const [type, listener] of this.events) {
      this.element.addEventListener(type, listener);
    }
  }

  disconnect() {
    for (const [type, listener] of this.events) {
      this.element.removeEventListener(type, listener);
    }
    this.removeTip();
  }

  show() {
    const { title, placement = 'top' } = this.element.dataset;
    if (!title) return;
    // 消える途中で出し直したときは、残っている要素をすぐ消して作り直す
    if (this.tip && !this.hideTimer) return;
    this.removeTip();

    this.tip = document.createElement('div');
    this.tip.id = `tooltip-${Math.random().toString(36).slice(2)}`;
    this.tip.className = `tooltip fade ${placement}`;
    this.tip.setAttribute('role', 'tooltip');
    const arrow = document.createElement('div');
    arrow.className = 'tooltip-arrow';
    const inner = document.createElement('div');
    inner.className = 'tooltip-inner';
    inner.textContent = title;
    this.tip.append(arrow, inner);
    // Bootstrap 3 の既定（container: false）と同じく要素の直後に入れ、モーダルやスクロールする枠の中でも要素と一緒に動かす
    this.element.after(this.tip);
    this.element.setAttribute('aria-describedby', this.tip.id);

    this.place(placement, arrow);
    this.tip.style.display = 'block';
    // 位置を決めるときにレイアウトが確定しているので、ここで in を付ければ fade のトランジションが起きる
    this.tip.classList.add('in');
  }

  hide() {
    if (!this.tip || this.hideTimer) return;
    this.tip.classList.remove('in');
    // Bootstrap 3 と同じく、.fade のトランジション（150ms）が終わってから消す
    this.hideTimer = setTimeout(() => this.removeTip(), TRANSITION_DURATION);
  }

  removeTip() {
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
    this.tip?.remove();
    this.tip = null;
    this.element.removeAttribute('aria-describedby');
  }

  // Bootstrap 3 と同じく、画面からはみ出すときは内側へずらし、矢印は要素の中央を指したままにする
  place(placement, arrow) {
    // top と left を 0 にしたときの位置を測り、どの要素を基準に配置されても画面上の座標で合わせる。margin は .tooltip.top などの矢印の分なので残す
    this.tip.style.top = '0px';
    this.tip.style.left = '0px';
    const origin = this.tip.getBoundingClientRect();
    const tipStyle = getComputedStyle(this.tip);
    const originTop = origin.top - Number.parseFloat(tipStyle.marginTop);
    const originLeft = origin.left - Number.parseFloat(tipStyle.marginLeft);

    const rect = this.element.getBoundingClientRect();
    const width = this.tip.offsetWidth;
    const height = this.tip.offsetHeight;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const vertical = placement === 'left' || placement === 'right';
    const setPosition = (top, left) => {
      this.tip.style.top = `${top - originTop}px`;
      this.tip.style.left = `${left - originLeft}px`;
    };

    let top;
    let left;
    if (vertical) {
      top = centerY - height / 2;
      left = placement === 'left' ? rect.left - width : rect.right;
    } else {
      top = placement === 'bottom' ? rect.bottom : rect.top - height;
      left = centerX - width / 2;
    }
    setPosition(top, left);

    // Bootstrap 3 と同じく、置いたあとの大きさで測り直す。端に寄ると幅が縮んで収まることがある
    const actualWidth = this.tip.offsetWidth;
    const actualHeight = this.tip.offsetHeight;
    if (placement === 'top' && actualHeight !== height) {
      top += height - actualHeight;
    }

    // Bootstrap 3 と同じく、画面からはみ出すときだけ内側へずらし、両端からはみ出すほど大きいときは左端（上端）に合わせる
    const clamp = (value, size, max) =>
      Math.max(Math.min(value, max - size), 0);
    if (vertical) {
      top = clamp(top, actualHeight, document.documentElement.clientHeight);
      arrow.style.top = `${centerY - top}px`;
    } else {
      left = clamp(left, actualWidth, document.documentElement.clientWidth);
      arrow.style.left = `${centerX - left}px`;
    }
    setPosition(top, left);
  }
}
